<div class="app-overview">
  <div class="app-view-toggle reports__tabs" role="tablist" [attr.aria-label]="i18n.t('reports.tabs.label')" (keydown)="navigateTabs($event)">
    <button id="reports-tab-aggregate" class="app-view-toggle__button" type="button" role="tab"
      aria-controls="reports-panel-aggregate" [attr.aria-selected]="activeTab() === 'aggregate'"
      [attr.tabindex]="activeTab() === 'aggregate' ? 0 : -1"
      [class.app-view-toggle__button--active]="activeTab() === 'aggregate'" (click)="selectTab('aggregate')">
      <span class="fa-solid fa-chart-column" aria-hidden="true"></span>
      {{ i18n.t('reports.tabs.aggregate') }}
    </button>
    <button id="reports-tab-locations" class="app-view-toggle__button" type="button" role="tab"
      aria-controls="reports-panel-locations" [attr.aria-selected]="activeTab() === 'locations'"
      [attr.tabindex]="activeTab() === 'locations' ? 0 : -1"
      [class.app-view-toggle__button--active]="activeTab() === 'locations'" (click)="selectTab('locations')">
      <span class="fa-solid fa-location-dot" aria-hidden="true"></span>
      {{ i18n.t('reports.tabs.locations') }}
    </button>
  </div>
<section id="reports-panel-locations" class="reports reports__panel" role="tabpanel" aria-labelledby="reports-tab-locations"
  [hidden]="activeTab() !== 'locations'" [attr.aria-busy]="loading() || rangeLoading()">
  <header class="app-overview__header reports__header">
    <div class="reports__heading">
      <h1 class="app-overview__title">{{ i18n.t('reports.title') }}</h1>
      <p class="app-overview__intro reports__context">
        @if (data()?.current_iteration; as current) {
          <span>{{ current.location_name }}</span><span aria-hidden="true">|</span>
          <span>{{ current.audit_name }}</span><span aria-hidden="true">|</span>
          <time [attr.datetime]="current.completed_at">{{ date(current.completed_at) }}</time>
          @if (current.completed_at_is_estimated) { <span>({{ i18n.t('reports.estimated') }})</span> }
        } @else if (selected(); as option) {
          <span>{{ option.location_name }}</span><span aria-hidden="true">|</span><span>{{ option.audit_name }}</span>
        } @else { {{ i18n.t('reports.intro') }} }
      </p>
    </div>
    <button class="primary-button primary-button--compact" type="button" (click)="loadOptions()" [disabled]="loading() || rangeLoading()">
      <span class="fa-solid fa-arrows-rotate" aria-hidden="true"></span> {{ i18n.t('reports.refresh') }}
    </button>
  </header>

  <div class="app-overview__filters reports__filters" [attr.aria-label]="i18n.t('reports.filters')" role="group">
    <label class="app-field reports__audit" for="report-audit">
      <span class="app-field__label">{{ i18n.t('reports.audit') }}</span>
      <select id="report-audit" class="app-select" [value]="selectedKey()" (change)="selectAudit($any($event.target).value)" [disabled]="optionsLoading() || !options().length">
        @if (!options().length) { <option value="">{{ i18n.t(optionsLoading() ? 'reports.loading' : 'reports.noOptions') }}</option> }
        @for (option of options(); track key(option)) {
          <option [value]="key(option)" [selected]="key(option) === selectedKey()">{{ option.audit_name }} — {{ option.location_name }}</option>
        }
      </select>
    </label>
    <label class="app-field reports__comparison" for="report-comparison">
      <span class="app-field__label">{{ i18n.t('reports.compare') }}</span>
      <select id="report-comparison" class="app-select" [value]="data()?.comparison_iteration?.execution_plan_id ?? ''" (change)="selectComparison($any($event.target).value)" [disabled]="loading() || rangeLoading() || !data()?.comparison_options?.length">
        @if (!data()?.comparison_options?.length) { <option value="">{{ i18n.t(loading() ? 'reports.loading' : 'reports.noComparison') }}</option> }
        @for (iteration of data()?.comparison_options ?? []; track iteration.execution_plan_id; let index = $index) {
          <option [value]="iteration.execution_plan_id" [selected]="iteration.execution_plan_id === data()?.comparison_iteration?.execution_plan_id">{{ comparisonLabel(iteration, index) }}</option>
        }
      </select>
    </label>
    <label class="app-field reports__range" [class.app-field--invalid]="rangeInvalid()" for="report-range">
      <span class="app-field__label">{{ i18n.t('reports.range') }}</span>
      <input id="report-range" type="number" min="1" [max]="rangeMax()" [disabled]="rangeDisabled()" step="1" inputmode="numeric" [value]="range()"
        (input)="$any($event.target).value = editRange($any($event.target).value)" (change)="commitRange()" (keydown.enter)="commitRange()"
        [attr.aria-invalid]="rangeInvalid()" [attr.aria-describedby]="rangeInvalid() ? 'report-range-error' : 'report-range-hint'" />
    </label>
  </div>
  <div class="reports__filter-notes">
    <p id="report-range-hint" class="app-overview__intro reports__hint" [attr.role]="rangeLoading() ? 'status' : null">{{ i18n.t(rangeLoading() ? 'reports.rangeLoading' : 'reports.rangeHint') }}</p>
    @if (!errorKey() && !loading() && data()?.current_iteration) {
      @if (data(); as header) {
        <p class="app-overview__intro reports__hint reports__history-count" role="status">
          {{ i18n.t('reports.historyCount') }} {{ header.iterations_returned }} / {{ header.total_completed_iterations }}.
          @if (!header.comparison_iteration) { {{ i18n.t(header.total_completed_iterations === 1 ? 'reports.singleIteration' : 'reports.rangeOne') }} }
        </p>
      }
    }
  </div>
  @if (rangeInvalid()) { <p id="report-range-error" class="form-message" role="alert">{{ i18n.t('reports.rangeError') }}</p> }
  @if (rangeUpdateError()) {
    <div class="form-message reports__message" role="alert"><span>{{ i18n.t(rangeUpdateError()) }}</span><button class="secondary-button secondary-button--compact" type="button" (click)="retryRange()">{{ i18n.t('reports.retry') }}</button></div>
  }

  @if (errorKey()) {
    <div class="form-message reports__message" role="alert">
      <span class="app-message__icon app-message__icon--error fa-solid fa-circle-exclamation" aria-hidden="true"></span>
      <span>{{ i18n.t(errorKey()) }}</span>
      <button class="secondary-button secondary-button--compact" type="button" (click)="retry()">{{ i18n.t('reports.retry') }}</button>
    </div>
  } @else if (loading()) {
    <p class="reports__message" role="status"><span class="fa-solid fa-spinner fa-spin" aria-hidden="true"></span>{{ i18n.t('reports.loading') }}</p>
  } @else if (!options().length) {
    <p class="app-empty-state" role="status">{{ i18n.t('reports.empty') }}</p>
  } @else if (data(); as header) {
    @if (!header.current_iteration) {
      <p class="app-empty-state" role="status">{{ i18n.t('reports.noHistory') }}</p>
    } @else {
      @if (reportUnavailable()) {
        <p class="form-message" role="status"><span class="app-message__icon app-message__icon--info fa-solid fa-circle-info" aria-hidden="true"></span>{{ i18n.t('reports.unavailable') }}</p>
      }
      @if (summaryLoading()) {
        <div class="app-panel reports__score-state" role="status"><span class="app-message__icon app-message__icon--loading fa-solid fa-spinner fa-spin" aria-hidden="true"></span>{{ i18n.t('reports.score.loading') }}</div>
      } @else if (summaryError()) {
        <div class="form-message reports__message" role="alert">
          <span>{{ i18n.t(summaryError()) }}</span>
          <button class="secondary-button secondary-button--compact" type="button" (click)="summaryError() === 'reports.score.changed' ? loadHeader() : loadSummary()">{{ i18n.t('reports.retry') }}</button>
        </div>
      } @else if (currentScore(); as score) {
        @defer (on immediate) {
          <div class="reports__kpis">
            <app-report-score-card [current]="score" [comparison]="comparisonScore()" [comparisonLabel]="selectedComparisonLabel()" />
            <app-report-status-kpis #statusKpis [option]="selected()!" [header]="header" [comparisonLabel]="selectedComparisonLabel()" (refresh)="loadHeader()" />
          </div>
          <div class="reports__charts">
            <app-report-trend [iterations]="summary()!.iterations" [totalCompleted]="summary()!.total_completed_iterations" />
            <app-report-status-distribution [option]="selected()!" [current]="score" (refresh)="loadHeader()" (reportIssueRequested)="reportIssueRequested.emit()" />
          </div>
          <app-report-areas #areasView [option]="selected()!" [header]="header" (refresh)="loadHeader()" />
          <app-report-narrative [header]="header" [current]="score" [comparison]="comparisonScore()" [comparisonLabel]="selectedComparisonLabel()" [answers]="statusKpis.reportState()" [areas]="areasView.reportState()" />
        } @placeholder {
          <div class="app-panel reports__score-state" role="status">{{ i18n.t('reports.score.loading') }}</div>
        } @error {
          <div class="form-message reports__message" role="alert">
            <span>{{ i18n.t('reports.score.chartError') }}</span>
            <button class="secondary-button secondary-button--compact" type="button" (click)="loadHeader()">{{ i18n.t('reports.retry') }}</button>
          </div>
        }
      }
    }
  }
</section>
<section id="reports-panel-aggregate" class="reports__panel" role="tabpanel" aria-labelledby="reports-tab-aggregate"
  [hidden]="activeTab() !== 'aggregate'">
    @defer (on immediate) {
      <app-report-aggregate (detailsRequested)="openLocationReport($event)" />
    } @placeholder {
      <p class="app-empty-state" role="status">{{ i18n.t('reports.loading') }}</p>
    } @error {
      <p class="form-message" role="alert">{{ i18n.t('reports.score.chartError') }}</p>
    }
</section>
</div>
